/** @jsxImportSource @emotion/react */

import { Circle, Square, Squircle } from 'lucide-react';
import { ModalButton, OpenerIconWrapper } from './styles';
import React, { useCallback, useState } from 'react';
import { selectActiveModal, selectFabricManager } from '../../../../store/editor/selectors';
import { useDispatch, useSelector } from 'react-redux';

import BaseModal from '../../elements/modals/base-modal';
import EmptyButton from 'ud-ui/components/empty-button';
import { ModalState } from '../../../../domain/interface/modal-state';
import { Object } from 'fabric/fabric-impl';
import SnapToGrid from 'ud-ui/components/fabric-canvas/addons/snap-to-grid';
import UDTippy from 'ud-ui/components/tippy';
import customization from 'ud-ui/components/fabric-canvas/addons/customization';
import { fabric } from 'fabric';
import history from 'ud-ui/components/fabric-canvas/addons/history';
import { setActiveModal } from '../../../../store/editor/editor';
import { useCloseOnOutsideOrEsc } from 'shared/utils';

type ShapeType = 'rect' | 'circle';

export function AddShape() {
  const dispatch = useDispatch();
  const activeModal = useSelector(selectActiveModal);
  const fabricManager = useSelector(selectFabricManager);
  const [isSelected, setSelected] = useState(false);
  const [modalState, setModalState] = useState<ModalState>({ isModalOpen: false, modalStyle: {} });

  const closeModals = useCallback(() => {
    setSelected(false);
    setModalState((prev) => ({ ...prev, isModalOpen: false }));
    dispatch(setActiveModal(undefined));
  }, [dispatch]);

  const openShapeModal = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const target = e.currentTarget;
      if (!target) return;

      const bounding = target.getBoundingClientRect();
      setModalState({
        isModalOpen: true,
        modalStyle: {
          top: `${bounding.top}px`,
          left: `${bounding.left + bounding.width + 15}px`,
          width: 300,
        },
      });
      setSelected(true);
      dispatch(setActiveModal('addShape'));
    },
    [dispatch],
  );

  useCloseOnOutsideOrEsc({
    isActive: isSelected,
    isInsideSelectors: ['.modal-button', '.base-modal', '.icon-button-shape'],
    onClose: closeModals,
  });

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (isSelected) {
        closeModals();
      } else {
        openShapeModal(e);
      }
    },
    [closeModals, isSelected, openShapeModal],
  );

  const addShape = useCallback(
    (shape: ShapeType) => {
      if (!fabricManager) return;

      history.saveState();
      const { left, top } = fabricManager.getLeftTopCurVP();
      let object: Object;

      switch (shape) {
        case 'rect':
          object = new fabric.Rect({
            width: 200,
            height: 100,
            fill: 'red',
            strokeWidth: 0,
            left,
            top,
            ...customization.getAllProperties(),
          });
          break;
        case 'circle':
          object = new fabric.Circle({
            radius: 100,
            fill: 'red',
            strokeWidth: 0,
            left,
            top,
            ...customization.getAllProperties(),
          });
          break;
      }

      fabricManager.canvas.add(object);
      SnapToGrid.snapToGrid([object]);
      closeModals();
    },
    [closeModals, fabricManager],
  );

  return (
    <>
      <UDTippy disabled={Boolean(activeModal)} content={'Фигуры'} size="xs">
        <span style={{ display: 'inline-block' }}>
          <EmptyButton
            className="icon-button-shape"
            onClick={handleClick}
            color={activeModal === 'addShape' ? '#D9B244' : undefined}
            icon={<OpenerIconWrapper icon={<Squircle size={20} strokeWidth={1.5} />} />}
            tabIndex={0}
          />
        </span>
      </UDTippy>
      <BaseModal className="base-modal" isOpen={modalState.isModalOpen && isSelected} style={modalState.modalStyle}>
        <ModalButton className="modal-button" onClick={() => addShape('rect')}>
          <Square size={20} strokeWidth={1} />
          Прямоугольник
        </ModalButton>
        <ModalButton className="modal-button" onClick={() => addShape('circle')}>
          <Circle size={20} strokeWidth={1} />
          Круг
        </ModalButton>
      </BaseModal>
    </>
  );
}
